// UserSearchInput — member search for share dialogs.
// Strategy: server-side search via BE `?search=` param (ILIKE on email + first/last name).
// BE drops queries shorter than 3 chars; FE also enforces min-prefix=3 + 300ms debounce
// so we never fire an HTTP call for stray characters.
// Portal target resolves to closest('[role="dialog"]') or document.body so Radix
// DismissableLayer treats option clicks as inside the modal.
// position:fixed dropdown with z-[var(--z-dropdown)]=60 to sit above modal layer.
import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import { useTranslations } from "use-intl";
import { createPortal } from "react-dom";
import { Search, X, Loader2 } from "lucide-react";
import Input from "@/components/form/input/InputField";
import { organizationService } from "@/lib/api/services/organization.service";
import type { OrgMember } from "@/types/organization.types";
import type { User } from "./share-permission.utils";
import { formatUserIdentifier, formatUserName } from "@/utils/format-user-name";

const MIN_PREFIX = 3;
const DEBOUNCE_MS = 300;
// Page size for autocomplete dropdown — matches typical UX (~20 visible rows).
const SEARCH_LIMIT = 20;

interface UserSearchInputProps {
  orgId: string;
  selectedUser: User | null;
  onSelectUser: (user: User) => void;
  onClearSelection: () => void;
  excludeUserIds: Set<string>;
}

interface OrgMemberMapped {
  userId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  username?: string | null;
  identifier: string;
}

function mapMember(m: OrgMember, unknownUser: string): OrgMemberMapped {
  const u = m.user;
  const displayName = formatUserName(u, { fallback: unknownUser });
  return {
    userId: m.userId,
    displayName,
    firstName: u?.firstName ?? undefined,
    lastName: u?.lastName ?? undefined,
    email: u?.email ?? undefined,
    username: u?.username ?? undefined,
    identifier: formatUserIdentifier(u, { fallback: "" }),
  };
}

export default function UserSearchInput({
  orgId,
  selectedUser,
  onSelectUser,
  onClearSelection,
  excludeUserIds,
}: UserSearchInputProps) {
  const t = useTranslations("permissions");
  const unknownUser = t("share.unknownUser");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [members, setMembers] = useState<OrgMemberMapped[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  // Debounce: update debouncedSearch DEBOUNCE_MS after the last keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  // Server-side search — fires only when debounced query reaches MIN_PREFIX.
  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!orgId || q.length < MIN_PREFIX) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const res = await organizationService.getMembers(orgId, {
          search: q,
          limit: SEARCH_LIMIT,
        });
        if (cancelled) return;
        setMembers((res.data ?? []).map((m) => mapMember(m, unknownUser)));
      } catch {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, debouncedSearch, unknownUser]);

  const filteredMembers = members.filter((m) => !excludeUserIds.has(m.userId));

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useLayoutEffect(() => {
    if (!isDropdownOpen) {
      setPortalTarget(null);
      return;
    }
    const dialog = containerRef.current?.closest<HTMLElement>('[role="dialog"]');
    setPortalTarget(dialog ?? document.body);
  }, [isDropdownOpen]);

  useLayoutEffect(() => {
    if (!isDropdownOpen) {
      setAnchorRect(null);
      return;
    }
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isDropdownOpen]);

  const handleSelect = (m: OrgMemberMapped) => {
    onSelectUser({
      id: m.userId,
      email: m.email ?? null,
      username: m.username ?? null,
      firstName: m.firstName,
      lastName: m.lastName,
    });
    setSearchInput("");
    setDebouncedSearch("");
    setIsDropdownOpen(false);
  };

  if (selectedUser) {
    const selectedLabel = formatUserName(
      {
        id: selectedUser.id,
        email: selectedUser.email,
        username: selectedUser.username,
        firstName: selectedUser.firstName ?? null,
        lastName: selectedUser.lastName ?? null,
        avatarUrl: null,
      },
      { fallback: unknownUser },
    );
    return (
      <div className="flex h-10 items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 dark:border-brand-800 dark:bg-brand-900/20">
        <div className="flex size-6 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
          {selectedLabel.charAt(0).toUpperCase()}
        </div>
        <span className="flex-1 truncate text-sm text-gray-800 dark:text-white">
          {selectedLabel}
        </span>
        <button
          type="button"
          onClick={() => {
            onClearSelection();
            setSearchInput("");
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="size-4" />
        </button>
      </div>
    );
  }

  const showDropdown = isDropdownOpen && anchorRect && portalTarget;
  const showHint =
    isDropdownOpen && searchInput.trim().length > 0 && searchInput.trim().length < MIN_PREFIX;
  const queryReady = debouncedSearch.trim().length >= MIN_PREFIX;

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-gray-400" />
      <Input
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          if (!isDropdownOpen) setIsDropdownOpen(true);
        }}
        onFocus={() => setIsDropdownOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setIsDropdownOpen(false);
        }}
        placeholder={t("share.searchMembers")}
        className="h-10 pl-9"
      />
      {showHint && (
        <p className="mt-1 text-xs text-gray-400">{t("share.searchHint", { min: MIN_PREFIX })}</p>
      )}
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            data-testid="member-dropdown"
            role="listbox"
            style={{
              position: "fixed",
              top: anchorRect.top + 4,
              left: anchorRect.left,
              width: anchorRect.width,
            }}
            className="z-[var(--z-dropdown,60)] max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
          >
            {!queryReady ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
                {t("share.searchHint", { min: MIN_PREFIX })}
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-4">
                <Loader2 className="size-4 animate-spin text-gray-400" />
                <span className="text-xs text-gray-500">{t("share.loadingMembers")}</span>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
                {t("share.noMatchingMembers")}
              </div>
            ) : (
              filteredMembers.map((m) => (
                <button
                  key={m.userId}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleSelect(m)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <div className="flex size-7 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
                    {m.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-800 dark:text-white/90">{m.displayName}</p>
                    <p className="truncate text-[10px] text-gray-400 dark:text-gray-500">
                      {m.identifier || t("share.noEmail")}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>,
          portalTarget,
        )}
    </div>
  );
}
